// ================================================================
// 貨幣 API 模組
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：多貨幣支援 API 通訊
// ================================================================

import { apiClient } from './client';

// ================================================================
// 類型定義
// ================================================================

export interface Currency {
  code: string;
  name: string;
  symbol: string;
  flag: string;
}

export interface ExchangeRate {
  currency_code: string;
  rate: number;           // 前端使用的欄位名
  rate_to_ntd?: number;   // 後端返回的欄位名
  updated_at?: string;
  last_updated_at?: string;
}

export interface CurrencyRatesResponse {
  success: boolean;
  rates: ExchangeRate[];
}

export interface ConvertCurrencyRequest {
  amount: number;
  from: string;
  to: string;
}

export interface ConvertCurrencyResponse {
  success: boolean;
  original_amount: number;
  from_currency: string;
  converted_amount: number;
  to_currency: string;
  rate: number;
}

export interface UserCurrencyResponse {
  success: boolean;
  preferred_currency: string;
}

// ================================================================
// 支援的貨幣列表
// ================================================================

export const SUPPORTED_CURRENCIES: Currency[] = [
  { code: 'NTD', name: '新台幣', symbol: 'NT$', flag: '🇹🇼' },
  { code: 'USD', name: '美元', symbol: '$', flag: '🇺🇸' },
  { code: 'EUR', name: '歐元', symbol: '€', flag: '🇪🇺' },
  { code: 'JPY', name: '日圓', symbol: '¥', flag: '🇯🇵' },
  { code: 'CNY', name: '人民幣', symbol: '¥', flag: '🇨🇳' },
];

// ================================================================
// API 函式
// ================================================================

/**
 * 獲取所有支援的貨幣列表
 */
export const getCurrencies = async (): Promise<Currency[]> => {
  return SUPPORTED_CURRENCIES;
};

/**
 * 獲取匯率
 */
export const getExchangeRates = async (): Promise<CurrencyRatesResponse> => {
  return apiClient.get('/api/currencies/rates');
};

/**
 * 轉換貨幣
 */
export const convertCurrency = async (
  amount: number,
  from: string,
  to: string
): Promise<ConvertCurrencyResponse> => {
  return apiClient.post('/api/currencies/convert', {
    amount,
    from,
    to,
  });
};

/**
 * 獲取用戶偏好貨幣
 */
export const getUserCurrency = async (): Promise<UserCurrencyResponse> => {
  return apiClient.get('/api/user/currency');
};

/**
 * 設定用戶偏好貨幣
 */
export const setUserCurrency = async (
  currencyCode: string
): Promise<{ success: boolean; message: string }> => {
  return apiClient.put('/api/user/currency', {
    currency_code: currencyCode,
  });
};

/**
 * 根據貨幣代碼獲取貨幣資訊
 */
export const getCurrencyInfo = (code: string): Currency | undefined => {
  return SUPPORTED_CURRENCIES.find((c) => c.code === code);
};

/**
 * 格式化價格顯示
 */
export const formatPrice = (
  amount: number,
  currencyCode: string,
  showSymbol: boolean = true
): string => {
  const currency = getCurrencyInfo(currencyCode);
  
  if (!currency) {
    return `${amount}`;
  }

  // 根據貨幣決定小數位數
  const decimals = currencyCode === 'JPY' ? 0 : 2;
  const formattedAmount = amount.toFixed(decimals);

  if (showSymbol) {
    // 日圓和人民幣符號放前面
    if (currencyCode === 'JPY' || currencyCode === 'CNY') {
      return `${currency.symbol}${formattedAmount}`;
    }
    return `${currency.symbol} ${formattedAmount}`;
  }

  return formattedAmount;
};
